import { generators, macroInputCaptureRequestSchema, macroInputCaptureReplySchema,
  type PlaycastMessage, type PlaycastMessageTarget, type MacroInputCaptureRequest } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../services/ipc/IIpcService';

/** Only the authorized recording peer receives its physical input batches. */
export class MacroInputCaptureRelay {
  private owner: { peerId: string; request: MacroInputCaptureRequest; local: boolean } | null = null;
  private pending = new Map<string, { peerId: string; timer: ReturnType<typeof setTimeout> }>();
  constructor(private readonly ipc: IIpcService, private readonly reply: (peerId: string, raw: string) => void) {}

  handle(peerId: string, raw: unknown, local: boolean): void {
    const parsed = macroInputCaptureRequestSchema.safeParse(raw);
    if (!parsed.success) return;
    const request = parsed.data;
    if (this.owner && (this.owner.peerId !== peerId || this.owner.request.takeId !== request.takeId)) {
      this.fail(peerId, request, 'input_capture_owned_by_another_recording'); return;
    }
    if (!this.owner && request.command !== 'start') {
      this.fail(peerId, request, 'input_capture_not_started'); return;
    }
    if (this.pending.has(request.requestId) || this.pending.size >= 8) {
      this.fail(peerId, request, 'input_capture_request_busy'); return;
    }
    this.owner = { peerId, request, local };
    const timer = setTimeout(() => {
      this.pending.delete(request.requestId);
      this.fail(peerId, request, 'Host input capture did not respond. Update/restart Playjector.');
      this.release(peerId);
    }, 5000);
    this.pending.set(request.requestId, { peerId, timer });
    try { this.ipc.send(generators.macroInputCapture.native('host', request, local)); }
    catch { clearTimeout(timer); this.pending.delete(request.requestId); this.fail(peerId, request, 'input_capture_send_failed'); this.release(peerId); }
  }

  receive(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    if (message.header.target !== 'record' || message.header.action !== 'inputs' || !message.header.isReply) return false;
    const parsed = macroInputCaptureReplySchema.safeParse(message.body.message);
    if (!parsed.success) return true;
    const result = parsed.data;
    const pending = this.pending.get(result.requestId);
    if (!pending || this.owner?.request.takeId !== result.takeId || this.owner.peerId !== pending.peerId) return true;
    clearTimeout(pending.timer); this.pending.delete(result.requestId);
    this.send(pending.peerId, result);
    if (!result.success || (result.stopped && !result.more)) this.release(pending.peerId);
    return true;
  }

  release(peerId: string): void {
    if (this.owner?.peerId !== peerId) return;
    const owner = this.owner; this.owner = null;
    for (const [id, pending] of this.pending) {
      clearTimeout(pending.timer); this.pending.delete(id);
    }
    // Stop is idempotent. No input producer survives a disconnected owner.
    try { this.ipc.send(generators.macroInputCapture.native('host', {
      ...owner.request, requestId: crypto.randomUUID(), command: 'stop',
    }, owner.local)); } catch { /* Native lease also expires without polls. */ }
  }
  private fail(peerId: string, request: MacroInputCaptureRequest, error: string): void {
    this.send(peerId, { requestId: request.requestId, takeId: request.takeId, success: false,
      events: [], elapsedMs: 0, stopped: true, more: false, error });
  }
  private send(peerId: string, message: unknown): void {
    this.reply(peerId, JSON.stringify({ header: { target: 'system', action: 'macroRecorderInputCapture', isReply: true, source: 'host' }, body: { message } }));
  }
}
